import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useClinicList } from 'infrastructure/hooks/clinic/use-clinic-list';
import { Clinic } from 'infrastructure/classes/clinic';
import BaseInput from 'components/atoms/base-input';
import SortOrderSelectBox from 'components/molecules/sort-order-select-box';
import { useTranslation } from 'react-i18next';
import BaseSpinWrapper from 'components/atoms/base-spin-wrapper';
import List from 'rc-virtual-list';
import { SortOrder } from 'infrastructure/enums';
import SearchIcon from 'components/atoms/icons/searchIcon';
import EmptyTable from 'components/atoms/base-table/report/empty';
import BaseSwitch from 'components/atoms/base-switch';

import s from './styles.module.scss';
import ClinicFilterFormListItem from './list-item';

type SetString = Set<string>;

export interface ISelectedClinics {
  all?: string[];
  included?: string[];
  parentClinicGuid?: string[];
}

interface IProps {
  agencyGuid?: string[];
  included?: string[];
  parentClinicGuid?: string[];
  onChange: (props: ISelectedClinics) => void;
  onStatsChange?: (stats: {
    total: number;
    parents: number;
    children: number;
  }) => void;
  showOnInteractiveTimeDashboard?: boolean;
  useAdminApi?: boolean;
}

const ClinicFilterForm: React.FC<IProps> = ({
  agencyGuid,
  included: includedParam = [],
  parentClinicGuid: parentClinicGuidParam = [],
  showOnInteractiveTimeDashboard,
  useAdminApi,
  onChange,
  onStatsChange,
}) => {
  const { t } = useTranslation();
  const {
    clinics: clinicsResponse,
    loadClinics,
    loading,
    meta,
  } = useClinicList({
    meta: {
      items: 9999999,
      showOnInteractiveTimeDashboard,
      useAdminApi,
      shortResponse: true,
    },
  });

  const clinics = useMemo(() => {
    if (!agencyGuid?.length) return clinicsResponse;
    return clinicsResponse.filter((c) =>
      agencyGuid.some((ag) => ag === c.agencyGuid),
    );
  }, [agencyGuid, clinicsResponse]);

  const allClinicGuids = useMemo<SetString>(
    () => new Set(clinics.map((el) => el.guid)),
    [clinics.length],
  );

  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.ASC);
  const [onlyParents, setOnlyParents] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSet, setSelectedSet] = useState<SetString>(new Set());
  const [lockedParents, setLockedParents] = useState<SetString>(new Set());

  const isChecked = (clinic: Clinic) => {
    if (clinic.parentClinicGuid && lockedParents.has(clinic.parentClinicGuid)) {
      return true;
    }
    return selectedSet.has(clinic.guid);
  };

  const findParentClinics = (from: Clinic[]) =>
    from.flatMap((el) =>
      el.parentClinicGuid
        ? clinics.filter((parent) => parent.guid === el.parentClinicGuid)
        : [],
    );

  const searchInList = (list: Clinic[], searchTerm?: string) => {
    if (!searchTerm) return list;
    const terms = searchTerm.trim().toLowerCase();
    return list.filter((clinic) =>
      clinic.legalname.toLowerCase().includes(terms),
    );
  };

  const groupedClinics = useMemo(() => {
    let clinicsToSort = searchInList(clinics, searchQuery);

    if (onlyParents) {
      clinicsToSort = clinicsToSort.filter(
        (c) => c.childClinicsGuid.length > 0 || c.parentClinicGuid === null,
      );
      return Clinic.sortByParent(clinicsToSort, sortOrder);
    }

    const parentClinics = findParentClinics(clinicsToSort);
    return Clinic.sortByParent([...parentClinics, ...clinicsToSort], sortOrder);
  }, [sortOrder, searchQuery, onlyParents, clinics.length]);

  const showEmptyState = !loading && groupedClinics.length === 0;

  const updateAll = (newSet: SetString, newLocked: SetString) => {
    setSelectedSet(new Set(newSet));
    setLockedParents(new Set(newLocked));

    onChange({
      included: Array.from(newSet),
      parentClinicGuid: Array.from(newLocked),
    });

    if (onStatsChange) {
      const individually = clinics.filter((c) => newSet.has(c.guid));
      const viaParent = clinics.filter(
        (c) => c.parentClinicGuid && newLocked.has(c.parentClinicGuid),
      );
      const all = [...individually, ...viaParent];
      onStatsChange({
        total: all.length,
        parents: all.filter((c) => !c.parentClinicGuid).length,
        children: all.filter((c) => !!c.parentClinicGuid).length,
      });
    }
  };

  const onSelect = useCallback(
    (checked: boolean, clinic: Clinic) => {
      const newSet = new Set(selectedSet);
      if (checked) newSet.add(clinic.guid);
      else newSet.delete(clinic.guid);
      updateAll(newSet, lockedParents);
    },
    [selectedSet, lockedParents],
  );

  const onChildrenSelect = useCallback(
    (checked: boolean, clinic: Clinic) => {
      const newSet = new Set(selectedSet);
      const newLocked = new Set(lockedParents);

      if (checked) {
        clinic.childClinicsGuid.forEach(({ guid }) => newSet.delete(guid));
        newLocked.add(clinic.guid);
      } else {
        newLocked.delete(clinic.guid);
      }

      updateAll(newSet, newLocked);
    },
    [selectedSet, lockedParents],
  );

  useEffect(() => {
    loadClinics(meta);
  }, []);

  useEffect(() => {
    updateAll(new Set(includedParam), new Set(parentClinicGuidParam));
  }, [allClinicGuids.size, includedParam.length, parentClinicGuidParam.length]);

  return (
    <BaseSpinWrapper spinning={loading}>
      <div className={s.form}>
        <div className={s.header}>
          <div className={s.block} data-cy="searchClinic-input">
            <BaseInput
              placeholder={t('labels.searchClinic')}
              onChange={(e) => setSearchQuery(e)}
              allowClear
              addonAfter={<SearchIcon />}
            />
          </div>
          <div className={s.block} data-cy="searchClinic-order">
            <SortOrderSelectBox value={sortOrder} onChange={setSortOrder} />
          </div>
          <div className={s.block} data-cy="searchClinic-only-parents">
            <BaseSwitch
              value={onlyParents}
              onChange={setOnlyParents}
              label={t('labels.onlyParent')}
              size="small"
            />
          </div>
        </div>

        <ul className={s.list}>
          {showEmptyState && <EmptyTable />}
          <List
            data={groupedClinics}
            height={showEmptyState ? 127 : 390}
            itemKey="guid"
          >
            {(clinic: Clinic) => (
              <>
                <ClinicFilterFormListItem
                  name={clinic.legalname}
                  checked={isChecked(clinic)}
                  isParent={!clinic.parentClinicGuid}
                  isLast={!clinic.childClinicsGuid.length}
                  withoutParent={clinic.withoutParent}
                  disabled={
                    !!clinic.parentClinicGuid &&
                    lockedParents.has(clinic.parentClinicGuid)
                  }
                  allChildrenChecked={lockedParents.has(clinic.guid)}
                  onChange={(checked) => onSelect(checked, clinic)}
                  onChildrenChange={(checked) =>
                    onChildrenSelect(checked, clinic)
                  }
                />
              </>
            )}
          </List>
        </ul>
      </div>
    </BaseSpinWrapper>
  );
};

export default ClinicFilterForm;
